import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { BracketScreen } from "@/components/BracketScreen";
import { useActiveEntry } from "@/hooks/use-active-entry";
import { setLastEntryId } from "@/lib/last-entry";
import {
  useLeagueStrict,
  useIsOverrideMode,
  useTargetUserId,
  useIsWatcher,
} from "@/lib/league-context";

const bracketSearchSchema = z.object({
  entry: z.string().uuid().optional(),
});

export const Route = createFileRoute("/_app/$league/bracket")({
  component: BracketPage,
  validateSearch: bracketSearchSchema,
  head: () => ({
    meta: [
      { title: "Fill Out Your Bracket — FIFA 2026" },
      {
        name: "description",
        content:
          "Predict every match of the FIFA 2026 knockout stage. Pick winners, predict goals, and lock in your bracket.",
      },
      { property: "og:title", content: "Fill Out Your Bracket — FIFA 2026" },
      {
        property: "og:description",
        content: "Build your bracket and predict the FIFA 2026 World Cup champion.",
      },
    ],
  }),
});

function BracketPage() {
  const { entry } = Route.useSearch();
  const { league } = Route.useParams();
  const isWatcher = useIsWatcher();
  const navigate = useNavigate();
  useEffect(() => {
    if (isWatcher) {
      navigate({ to: "/$league", params: { league }, replace: true });
    }
  }, [isWatcher, league, navigate]);
  if (isWatcher) return null;
  return <BracketEditorGate entry={entry} />;
}

// Multi-entry refactor (slice 1): once entries resolve, ensure the URL pins
// a specific `?entry=<id>`. Avoids hidden server-side default resolution and
// lets all downstream code treat entryId as known.
function BracketEditorGate({ entry }: { entry: string | undefined }) {
  const league = useLeagueStrict();
  const isOverride = useIsOverrideMode();
  const targetUserId = useTargetUserId();
  const navigate = useNavigate();

  const { activeEntryId, ready } = useActiveEntry({
    leagueId: league.id,
    isOverride,
    targetUserId,
    pinnedEntryId: entry,
  });

  useEffect(() => {
    if (!ready) return;
    if (entry) return;
    if (!activeEntryId) return;
    void navigate({
      to: "/$league/bracket",
      params: { league: league.slug },
      search: { entry: activeEntryId },
      replace: true,
    });
  }, [ready, entry, activeEntryId, navigate, league.slug]);

  // Zero-entry self user → push to the create-entry flow. Server pick fns
  // now require an explicit entryId so we can't render the editor without one.
  useEffect(() => {
    if (!ready) return;
    if (isOverride) return;
    if (activeEntryId) return;
    void navigate({
      to: "/$league/entries/new",
      params: { league: league.slug },
      replace: true,
    });
  }, [ready, isOverride, activeEntryId, navigate, league.slug]);

  // Remember which entry the user was last viewing in this league so the
  // Knockout/Groups tabs deep-link back here on next visit. Self mode only.
  useEffect(() => {
    if (isOverride) return;
    const id = entry ?? activeEntryId;
    if (!id) return;
    setLastEntryId(league.id, id);
  }, [isOverride, entry, activeEntryId, league.id]);

  // Post-lock, the editor has nothing left to do — redirect to the member
  // view, which already renders the locked bracket with correct/incorrect/
  // busted overlays, champion path, possible-points haircut, etc. Super
  // admins in override mode keep the editor so they can still mutate picks
  // via the override flow.
  const resolvedEntryId = entry ?? activeEntryId;
  const isPostLock =
    !!league.bracketLockAt && new Date(league.bracketLockAt) <= new Date();
  useEffect(() => {
    if (!ready) return;
    if (isOverride) return;
    if (!isPostLock) return;
    if (!resolvedEntryId) return;
    void navigate({
      to: "/$league/members/$entryId",
      params: { league: league.slug, entryId: resolvedEntryId },
      replace: true,
    });
  }, [ready, isOverride, isPostLock, resolvedEntryId, navigate, league.slug]);

  if (!ready || !resolvedEntryId) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Loading bracket…
      </div>
    );
  }
  if (isPostLock && !isOverride) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Loading bracket…
      </div>
    );
  }
  return <BracketScreen entryId={resolvedEntryId} />;
}
